export interface GeoPoint {
  latitude: number;
  longitude: number;
  address?: string;
  label?: string;
}

export interface TourLine {
  order_id: string;
  product_id?: string;
  product_name: string;
  flavor_label?: string | null;
  variant_name?: string | null;
  quantity: number;
  paid: number;
  free: number;
  delivered: number;
  promotion_type: string;
  lieferschein_number?: string | null;
  ls_status?: string | null;
}

export interface TourStop {
  id: string;
  pos_id: string;
  sequence_order: number;
  status: 'pending' | 'delivered' | 'skipped';
  skip_reason?: string | null;
  skip_note?: string | null;
  customer_name: string;
  customer_address: string;
  latitude: number | null;
  longitude: number | null;
  contact_person_name?: string | null;
  contact_person_phone?: string | null;
  phone?: string | null;
  items: TourLine[];
  lieferschein_numbers: string[];
  origin_visit_id?: string | null;
  legacy?: boolean;
}

export interface DeliveryTour {
  id: string;
  batch_name: string;
  planned_delivery_date: string;
  status: 'pending' | 'in_progress' | 'completed';
  is_archived?: boolean;
  total_distance_km?: number;
  total_stops?: number;
  starting_point?: GeoPoint | string | null;
  ending_point?: GeoPoint | string | null;
  stops: TourStop[];
  visits?: TourStop[];
}

export function parseGeo(value: unknown): GeoPoint | null {
  if (!value) return null;
  let raw: GeoPoint;
  try {
    raw =
      typeof value === 'string' ? (JSON.parse(value) as GeoPoint) : (value as GeoPoint);
  } catch {
    return null;
  }
  const latitude = Number(raw.latitude);
  const longitude = Number(raw.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }
  return { ...raw, latitude, longitude };
}

interface QtySplit {
  delivered: number;
  paid: number;
  free: number;
}

function addItemQty(target: QtySplit, item: TourLine): void {
  target.delivered += item.delivered || item.quantity;
  target.paid += item.paid || 0;
  target.free += item.free || 0;
}

export function formatPackQty(v: QtySplit): string {
  return v.free > 0
    ? `${v.delivered} (${v.paid}+${v.free})`
    : String(v.delivered);
}

export function qtyLabel(item: TourLine): string {
  if (item.free > 0) {
    return `${item.delivered} (${item.paid}+${item.free})`;
  }
  return String(item.delivered || item.quantity);
}

export interface PackListRow {
  key: string;
  label: string;
  packed: string;
}

export interface PackListStore {
  posId: string;
  name: string;
  lines: { orderId: string; qty: string; label: string }[];
}

export function tourPackRows(stops: TourStop[]): PackListRow[] {
  const packed = new Map<string, QtySplit>();
  for (const stop of stops) {
    for (const item of stop.items) {
      const key = `${item.product_id || ''}|${lineLabel(item)}`;
      const row = packed.get(key) || { delivered: 0, paid: 0, free: 0 };
      addItemQty(row, item);
      packed.set(key, row);
    }
  }
  return [...packed.entries()].map(([key, v]) => ({
    key,
    label: key.slice(key.indexOf('|') + 1),
    packed: formatPackQty(v),
  }));
}

export function tourPackStores(stops: TourStop[]): PackListStore[] {
  return [...stops]
    .sort((a, b) => a.sequence_order - b.sequence_order)
    .filter((stop) => stop.items.length > 0)
    .map((stop) => ({
      posId: stop.pos_id,
      name: stop.customer_name,
      lines: stop.items.map((item) => ({
        orderId: item.order_id,
        qty: qtyLabel(item),
        label: lineLabel(item),
      })),
    }));
}

export function lineLabel(item: TourLine): string {
  return item.flavor_label || item.variant_name || item.product_name;
}

export function mapsUrl(stop: TourStop): string {
  if (stop.latitude == null || stop.longitude == null) return '#';
  return `https://www.google.com/maps/dir/?api=1&destination=${stop.latitude},${stop.longitude}&travelmode=driving`;
}

/** Google Maps URL API: 9 waypoints + destination (origin omitted = GPS). */
export const MAPS_REMAINING_STOP_CAP = 10;

export function pendingStopsWithGeo(stops: TourStop[]): TourStop[] {
  return stops
    .filter(
      (s) =>
        s.status === 'pending' &&
        s.latitude != null &&
        s.longitude != null
    )
    .sort((a, b) => a.sequence_order - b.sequence_order);
}

function mapsDirUrl(destination: string, waypoints: string): string {
  return (
    `https://www.google.com/maps/dir/?api=1&destination=${destination}` +
    (waypoints ? `&waypoints=${waypoints}` : '') +
    '&travelmode=driving'
  );
}

export function remainingMapsUrl(
  stops: TourStop[],
  end?: GeoPoint | null
): {
  url: string | null;
  truncated: boolean;
  count: number;
  totalPending: number;
} {
  const pending = pendingStopsWithGeo(stops);
  if (pending.length === 0) {
    return { url: null, truncated: false, count: 0, totalPending: 0 };
  }
  if (end) {
    const truncated = pending.length > MAPS_REMAINING_STOP_CAP - 1;
    const items = pending.slice(0, MAPS_REMAINING_STOP_CAP - 1);
    const destination = `${end.latitude},${end.longitude}`;
    const waypoints = items
      .map((s) => `${s.latitude},${s.longitude}`)
      .join('|');
    return {
      url: mapsDirUrl(destination, waypoints),
      truncated,
      count: items.length,
      totalPending: pending.length,
    };
  }
  const truncated = pending.length > MAPS_REMAINING_STOP_CAP;
  const items = pending.slice(0, MAPS_REMAINING_STOP_CAP);
  const last = items[items.length - 1];
  const destination = `${last.latitude},${last.longitude}`;
  const waypoints = items
    .slice(0, -1)
    .map((s) => `${s.latitude},${s.longitude}`)
    .join('|');
  return {
    url: mapsDirUrl(destination, waypoints),
    truncated,
    count: items.length,
    totalPending: pending.length,
  };
}
